<script setup>
import axios from '@/axios'
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notifications'
import accessibilityIcon from '@/assets/icons/accessibility.svg?url'

const open = ref(false)
const isSaving = ref(false)
const activePanel = ref('display')

const auth = useAuthStore()
const notifications = useNotificationStore()

const settings = reactive({
  theme: 'system',
  high_contrast: false,
  colorblind_mode: 'none',
  font_scale: 1.0,
  reduced_motion: false,
  dyslexia_friendly_font: false,

  tts_enabled: false,
  tts_voice: null,
  tts_rate: 1.0,
  tts_pitch: 1.0,
  tts_volume: 1.0
})

const colorblindOptions = [
  { label: 'None', value: 'none' },
  { label: 'Deuteranopia', value: '2' },
  { label: 'Protanopia', value: '3' },
  { label: 'Tritanopia', value: '4' }
]

const fontScaleOptions = [
  { label: 'Small (90%)', value: 0.9 },
  { label: 'Default (100%)', value: 1.0 },
  { label: 'Large (110%)', value: 1.1 },
  { label: 'Larger (120%)', value: 1.2 },
  { label: 'XL (130%)', value: 1.3 },
  { label: 'XXL (140%)', value: 1.4 },
  { label: 'Max (150%)', value: 1.5 }
]

const ttsSupported = computed(() => {
  return (
    typeof window !== 'undefined' &&
    'speechSynthesis' in window &&
    'SpeechSynthesisUtterance' in window
  )
})

const voices = ref([])

onMounted(async () => {
  loadSavedLocalSettings()
  applyAccessibilitySettings()
  loadVoices()

  if (ttsSupported.value) {
    window.speechSynthesis.onvoiceschanged = loadVoices
  }

  window.addEventListener('keydown', handleKeydown)

  await loadSettings()
})

onUnmounted(() => {
  if (ttsSupported.value) {
    window.speechSynthesis.cancel()
    window.speechSynthesis.onvoiceschanged = null
  }

  window.removeEventListener('keydown', handleKeydown)
})

watch(
  settings,
  () => {
    applyAccessibilitySettings()
    saveLocalSettings()
  },
  { deep: true }
)

function toggleAccessibility() {
  open.value = !open.value
}

function closeAccessibility() {
  open.value = false
}

function setActivePanel(panel) {
  activePanel.value = panel
}

function handleKeydown(event) {
  if (event.key === 'Escape') {
    closeAccessibility()
  }
}

function handleTriggerKeydown(event) {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    toggleAccessibility()
  }
}

function iconStyle(icon) {
  return {
    backgroundColor: 'currentColor',
    WebkitMaskImage: `url("${icon}")`,
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskImage: `url("${icon}")`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain'
  }
}

function loadVoices() {
  if (!ttsSupported.value) return

  voices.value = window.speechSynthesis.getVoices() || []

  if (!settings.tts_voice && voices.value.length) {
    settings.tts_voice = voices.value[0]?.name ?? null
  }
}

function testVoice() {
  if (!ttsSupported.value || !settings.tts_enabled) return

  const utter = new SpeechSynthesisUtterance(
    'This is a test of the website reader voice.'
  )

  const selectedVoice = voices.value.find(voice => {
    return voice.name === settings.tts_voice
  })

  if (selectedVoice) {
    utter.voice = selectedVoice
  }

  utter.rate = Number(settings.tts_rate || 1.0)
  utter.pitch = Number(settings.tts_pitch || 1.0)
  utter.volume = Number(settings.tts_volume || 1.0)

  window.speechSynthesis.cancel()
  window.speechSynthesis.speak(utter)
}

async function loadSettings() {
  if (!auth.user_detail_id) return

  try {
    const res = await axios.get(
      `/api/family_members/${auth.user_detail_id}/settings`
    )

    Object.assign(settings, {
      theme: res.data?.theme ?? settings.theme,
      high_contrast: Boolean(
        res.data?.high_contrast ?? settings.high_contrast
      ),
      colorblind_mode: String(
        res.data?.colorblind_mode ?? settings.colorblind_mode
      ),
      font_scale: Number(
        res.data?.font_scale ?? settings.font_scale
      ),
      reduced_motion: Boolean(
        res.data?.reduced_motion ?? settings.reduced_motion
      ),
      dyslexia_friendly_font: Boolean(
        res.data?.dyslexia_friendly_font ??
          settings.dyslexia_friendly_font
      ),

      tts_enabled: Boolean(
        res.data?.tts_enabled ?? settings.tts_enabled
      ),
      tts_voice: res.data?.tts_voice ?? settings.tts_voice,
      tts_rate: Number(res.data?.tts_rate ?? settings.tts_rate),
      tts_pitch: Number(res.data?.tts_pitch ?? settings.tts_pitch),
      tts_volume: Number(res.data?.tts_volume ?? settings.tts_volume)
    })
  } catch (err) {
    console.error('Failed to load accessibility settings:', err)
  }
}

async function saveSettings() {
  if (!auth.user_detail_id) return

  isSaving.value = true

  try {
    await axios.put(
      `/api/family_members/${auth.user_detail_id}/settings`,
      {
        theme: settings.theme,
        high_contrast: settings.high_contrast,
        colorblind_mode: settings.colorblind_mode,
        font_scale: settings.font_scale,
        reduced_motion: settings.reduced_motion,
        dyslexia_friendly_font: settings.dyslexia_friendly_font,

        tts_enabled: settings.tts_enabled,
        tts_voice: settings.tts_voice,
        tts_rate: settings.tts_rate,
        tts_pitch: settings.tts_pitch,
        tts_volume: settings.tts_volume
      }
    )

    notifications.success?.('Accessibility settings saved.')
    closeAccessibility()
  } catch (err) {
    console.error('Failed to save accessibility settings:', err)
    notifications.error?.('Unable to save accessibility settings.')
  } finally {
    isSaving.value = false
  }
}

function saveLocalSettings() {
  localStorage.setItem(
    'accessibility_settings',
    JSON.stringify({
      theme: settings.theme,
      high_contrast: settings.high_contrast,
      colorblind_mode: settings.colorblind_mode,
      font_scale: settings.font_scale,
      reduced_motion: settings.reduced_motion,
      dyslexia_friendly_font: settings.dyslexia_friendly_font
    })
  )
}

function loadSavedLocalSettings() {
  const raw = localStorage.getItem('accessibility_settings')
  if (!raw) return

  try {
    Object.assign(settings, JSON.parse(raw))
    settings.colorblind_mode = String(settings.colorblind_mode ?? 'none')
    settings.font_scale = Number(settings.font_scale ?? 1.0)
  } catch {
    localStorage.removeItem('accessibility_settings')
  }
}

function applyAccessibilitySettings() {
  const root = document.documentElement

  if (settings.theme === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.dataset.theme = settings.theme
  }

  root.dataset.highContrast = settings.high_contrast ? 'true' : 'false'
  root.dataset.colorblindMode = settings.colorblind_mode
  root.dataset.reducedMotion = settings.reduced_motion ? 'true' : 'false'
  root.dataset.dyslexiaFont = settings.dyslexia_friendly_font ? 'true' : 'false'

  root.style.setProperty('--font-scale', String(settings.font_scale))
}
</script>

<template>
  <div class="accessibility-widget">
    <span
      role="button"
      tabindex="0"
      class="accessibility-trigger"
      data-tutorial="accessibility-trigger"
      :aria-expanded="open"
      aria-controls="accessibility-panel"
      aria-label="Accessibility settings"
      title="Accessibility settings"
      @click="toggleAccessibility"
      @keydown="handleTriggerKeydown"
    >
      <span
        class="accessibility-trigger-icon"
        :style="iconStyle(accessibilityIcon)"
        aria-hidden="true"
      />
    </span>

    <section
      v-if="open"
      id="accessibility-panel"
      class="bottom left floating large panel"
      data-tutorial="accessibility-panel"
      aria-label="Accessibility settings"
    >
      <div class="stacked gap-md">
        <header class="between cluster">
          <div class="stacked gap-xs">
            <h2>
              Accessibility
            </h2>

            <p class="small muted text">
              Display, motion, readability, and reader settings.
            </p>
          </div>

          <div class="end cluster">
            <div
              class="cluster"
              role="tablist"
              aria-label="Accessibility sections"
            >
              <button
                type="button"
                class="tab"
                data-tutorial="accessibility-display"
                :class="{ active: activePanel === 'display' }"
                role="tab"
                :aria-selected="activePanel === 'display'"
                @click="setActivePanel('display')"
              >
                Display
              </button>

              <button
                type="button"
                class="tab"
                data-tutorial="accessibility-reader"
                :class="{ active: activePanel === 'reader' }"
                role="tab"
                :aria-selected="activePanel === 'reader'"
                @click="setActivePanel('reader')"
              >
                Reader
              </button>
            </div>

            <button
              type="button"
              class="ghost button large text"
              aria-label="Close accessibility settings"
              @click="closeAccessibility"
            >
              ×
            </button>
          </div>
        </header>

        <section
          v-if="activePanel === 'display'"
          class="stacked gap-md"
        >
          <div class="three grid gap-md">
            <label class="field">
              Theme

              <select
                v-model="settings.theme"
                class="input"
              >
                <option value="system">System</option>
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
            </label>

            <label class="field">
              Font Size

              <select
                v-model.number="settings.font_scale"
                class="input"
              >
                <option
                  v-for="option in fontScaleOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>

            <label class="field">
              Colorblind Mode

              <select
                v-model="settings.colorblind_mode"
                class="input"
              >
                <option
                  v-for="option in colorblindOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
          </div>

          <div class="three grid gap-md">
            <div class="inset panel between cluster">
              <div class="stacked gap-xs">
                <div class="text-strong">High Contrast</div>
                <div class="small muted text">
                  Increase contrast across the site.
                </div>
              </div>

              <input
                v-model="settings.high_contrast"
                type="checkbox"
              >
            </div>

            <div class="inset panel between cluster">
              <div class="stacked gap-xs">
                <div class="text-strong">Reduced Motion</div>
                <div class="small muted text">
                  Minimize animations and transitions.
                </div>
              </div>

              <input
                v-model="settings.reduced_motion"
                type="checkbox"
              >
            </div>

            <div class="inset panel between cluster">
              <div class="stacked gap-xs">
                <div class="text-strong">Dyslexia Friendly Font</div>
                <div class="small muted text">
                  Improve character distinction.
                </div>
              </div>

              <input
                v-model="settings.dyslexia_friendly_font"
                type="checkbox"
              >
            </div>
          </div>
        </section>

        <section
          v-if="activePanel === 'reader'"
          class="stacked gap-md"
        >
          <div class="two grid gap-md">
            <div class="inset panel between cluster">
              <div class="stacked gap-xs">
                <div class="text-strong">Enable Reader</div>
                <div class="small muted text">
                  Read selected page content aloud.
                </div>
              </div>

              <input
                v-model="settings.tts_enabled"
                type="checkbox"
                :disabled="!ttsSupported"
              >
            </div>

            <label class="field">
              Voice

              <select
                v-model="settings.tts_voice"
                class="input"
                :disabled="!ttsSupported || !settings.tts_enabled"
              >
                <option
                  v-if="!voices.length"
                  :value="null"
                >
                  No voices available
                </option>

                <option
                  v-for="voice in voices"
                  :key="voice.name"
                  :value="voice.name"
                >
                  {{ voice.name }} ({{ voice.lang }})
                </option>
              </select>
            </label>
          </div>

          <div class="three grid gap-md">
            <label class="field">
              Rate

              <input
                v-model.number="settings.tts_rate"
                type="number"
                min="0.5"
                max="2"
                step="0.1"
                class="input"
                :disabled="!ttsSupported || !settings.tts_enabled"
              >
            </label>

            <label class="field">
              Pitch

              <input
                v-model.number="settings.tts_pitch"
                type="number"
                min="0.5"
                max="2"
                step="0.1"
                class="input"
                :disabled="!ttsSupported || !settings.tts_enabled"
              >
            </label>

            <label class="field">
              Volume

              <input
                v-model.number="settings.tts_volume"
                type="number"
                min="0"
                max="1"
                step="0.1"
                class="input"
                :disabled="!ttsSupported || !settings.tts_enabled"
              >
            </label>
          </div>

          <button
            class="secondary outline button"
            type="button"
            :disabled="!ttsSupported || !settings.tts_enabled"
            @click="testVoice"
          >
            Test Voice
          </button>
        </section>

        <footer class="between cluster">
          <button
            type="button"
            class="ghost button"
            @click="closeAccessibility"
          >
            Close
          </button>

          <button
            type="button"
            class="primary solid button"
            :disabled="isSaving"
            @click="saveSettings"
          >
            {{ isSaving ? 'Saving...' : 'Save Settings' }}
          </button>
        </footer>
      </div>
    </section>
  </div>
</template>
